// The quick wheel (ui-map-interface.md §8 "Quick chat", R1607, R1610,
// R1611; mockup round 077, card 01 "The wheel").
//
// The twelve quick lines on a ring of sectors round a hub. The hub is the
// channel switch: SQUAD on top, FACTION under it, and a slide through a
// half picks it. Line 1 stands at `start_deg` (the lower left) and the
// lines run clockwise, so the answers stand at the
// right. A line the sender cannot send greys out (`quickRef` answers null)
// and a release over it sends nothing, as a release in the hub or off the
// wheel sends nothing. While a sector is hot, the line under the wheel
// spells the line and the position it carries, or what it needs.
//
// Two gestures open it:
//
// - **Telephone.** A long press on the VIEW button of the map buttons opens
//   the wheel over the middle of the map (`installViewHold`, R1747). The
//   thumb slides onto a sector and lets go. A touch press shorter than the
//   long press opens or closes the VIEW panel at the release (R1610).
// - **Desktop.** Hold the comma: the wheel opens at the pointer with the
//   key of each sector over its word. A release of the comma over a sector,
//   or a press of a sector's key, sends. The keys are `[keys.quick]` and
//   read the key POSITION (R1611).
//
// **It is DOM** (the canvas/DOM split of §1): one SVG drawn when the wheel
// opens, and a move changes two classes and one line of text (§10, cost
// class "free"). It stands in `#ui`, so the UI scale scales it, and every
// point is turned into chrome px (R1556).

import { installHold } from './hold';
import { QUICK_LINES } from '../gen/balance';
import { KEYS, QUICK_WHEEL } from '../gen/presentation';
import { quickchat as S } from '../gen/strings';
import { matchKey } from '../keys';
import {
  QUICK_COUNT, posText, quickLineOf, quickNeeds, quickRef, quickText, quickWord,
  type PosLookup, type QuickCtx, type QuickPick,
} from './quickchat';
import { pressedControl } from './pressroute';
import { chromeViewport, toChromePx } from './uiscale';

/** The two channels a quick line goes to (the owner's ruling). */
export type QuickChannel = 'squad' | 'faction';

/** The geometry of one wheel, in chrome px. */
export interface WheelFig {
  outer: number;
  inner: number;
  gap: number;
  sectorGap: number;
  startDeg: number;
}

/** The wheel of a desktop and of a telephone. */
export function wheelFig(sheet: boolean): WheelFig {
  const w = QUICK_WHEEL;
  return {
    outer: sheet ? w.sheetOuterPx : w.outerPx,
    inner: sheet ? w.sheetInnerPx : w.innerPx,
    gap: w.gapPx,
    sectorGap: w.sectorGapRad,
    startDeg: w.startDeg,
  };
}

/** What a point names: a line (1-based), one half of the hub, or nothing. */
export type WheelHit = { line: number } | { hub: QuickChannel } | null;

/** The span of one sector, in degrees. */
const SPAN = 360 / QUICK_COUNT;

/**
 * What a point at (dx, dy) from the centre names. The hub and the ring
 * between it and the sectors are the channel switch; past the outer edge
 * is off the wheel. The angle runs clockwise from the east, because the
 * screen's y runs down.
 */
export function wheelHit(dx: number, dy: number, f: WheelFig): WheelHit {
  const d = Math.hypot(dx, dy);
  if (d > f.outer) return null;
  if (d <= f.inner + f.gap) return { hub: dy < 0 ? 'squad' : 'faction' };
  const deg = (Math.atan2(dy, dx) * 180) / Math.PI;
  const rel = (((deg - (f.startDeg - SPAN / 2)) % 360) + 360) % 360;
  return { line: Math.min(QUICK_COUNT, Math.floor(rel / SPAN) + 1) };
}

/** The middle of the sector of line n, in degrees. */
export function sectorDeg(n: number, f: WheelFig): number {
  return f.startDeg + SPAN * (n - 1);
}

/**
 * Where the wheel stands for a wished centre: inside a window of `vw` ×
 * `vh` chrome px, `edge` px from each side, with room for the line under
 * it. A window too small for the wheel centres it.
 */
export function wheelCentre(
  px: number, py: number, vw: number, vh: number, f: WheelFig, edge: number, below: number,
): { x: number; y: number } {
  const clamp = (v: number, lo: number, hi: number) => (hi < lo ? (lo + hi) / 2 : Math.min(hi, Math.max(lo, v)));
  return {
    x: clamp(px, f.outer + edge, vw - f.outer - edge),
    y: clamp(py, f.outer + edge, vh - f.outer - edge - below),
  };
}

/** What the wheel needs of the page. */
export interface QuickWheelDeps {
  /** The `#ui` element the wheel stands in. */
  host: HTMLElement;
  /** What the sender can name, read when the wheel opens. */
  ctx(): QuickCtx;
  /** What a position reads, for the line under the wheel. */
  look: PosLookup;
  /** The channel of the open chat tab when it is SQUAD or FACTION. */
  tabChannel(): QuickChannel | null;
  /** Whether the seat sits in a squad: SQUAD needs one. */
  hasSquad(): boolean;
  /** Sends one quick line. */
  send(line: number, channel: QuickChannel, pick: QuickPick): void;
  /** The sheet form factor of a telephone. */
  sheet(): boolean;
}

/** How the open wheel is driven: the comma, or a finger on the chip. */
type Mode = 'key' | 'touch';

const SVG = 'http://www.w3.org/2000/svg';

export class QuickWheel {
  private el: HTMLElement | null = null;
  private mode: Mode | null = null;
  private pointerId: number | null = null;
  private fig: WheelFig = wheelFig(false);
  private cx = 0;
  private cy = 0;
  private picks: (QuickPick | null)[] = [];
  private sectors: SVGPathElement[] = [];
  /** The word lines of every sector, with the line number in `data-n`. */
  private wordEls: SVGTextElement[] = [];
  /** The two halves of the hub and their words, by channel. */
  private halves: Record<QuickChannel, SVGElement[]> = { squad: [], faction: [] };
  private lineEl: SVGTextElement | null = null;
  private hotLine = 0;
  private chan: QuickChannel = 'squad';
  /** The channel the last wheel ended on, for the next one of the session. */
  private lastChan: QuickChannel | null = null;
  /** The wheel of the QUICK press stays open after a release with no line
   *  under it, and the next press on a line sends it (R1799). */
  private sticky = false;
  /** The last mouse position, in client px: the comma opens the wheel there. */
  private mouse: { x: number; y: number } | null = null;

  constructor(private readonly d: QuickWheelDeps) {
    window.addEventListener('pointermove', this.onMouse, { passive: true });
  }

  /** Is the wheel on the screen? */
  get isOpen(): boolean { return this.el !== null; }
  /** The line under the pointer, 0 for none. The tests read it. */
  get hot(): number { return this.hotLine; }
  /** The channel the wheel sends to. The tests read it. */
  get channel(): QuickChannel { return this.chan; }
  /** The element of the open wheel, or null. The tests read it. */
  get element(): HTMLElement | null { return this.el; }
  /** The centre of the open wheel, in chrome px. */
  get centre(): { x: number; y: number } { return { x: this.cx, y: this.cy }; }

  /** The comma (R1611): the wheel opens at the pointer, with the keys. */
  keyOpen(): void {
    if (this.el) return;
    const v = chromeViewport();
    const at = this.mouse
      ? { x: toChromePx(this.mouse.x), y: toChromePx(this.mouse.y) }
      : { x: v.w / 2, y: v.h / 2 };
    this.open('key', at.x, at.y);
    window.addEventListener('keydown', this.onKeyDown, true);
    window.addEventListener('keyup', this.onKeyUp, true);
    window.addEventListener('pointermove', this.onMove, true);
    window.addEventListener('blur', this.onBlur);
  }

  /** The long press of the VIEW button (R1610, R1747): the wheel opens
   *  over the middle of the map, and the finger of `pointerId` drives it. */
  touchOpen(pointerId: number): void {
    if (this.el) this.close();
    const v = chromeViewport();
    this.open('touch', v.w / 2, v.h * QUICK_WHEEL.sheetCentreY);
    this.pointerId = pointerId;
    window.addEventListener('pointermove', this.onMove, true);
    window.addEventListener('pointerup', this.onUp, true);
    window.addEventListener('pointercancel', this.onCancel, true);
  }

  /**
   * The QUICK press of the chat input (R1799, card 18 of round 085): the
   * wheel opens over the middle of the map, and the finger or the mouse
   * button of `pointerId` drives it as the long press of VIEW does. A
   * release with no line under it leaves the wheel open, so a tap opens it
   * and a second press on a line sends that line; a press outside every
   * line closes it.
   */
  pressOpen(pointerId: number): void {
    this.touchOpen(pointerId);
    this.sticky = true;
  }

  /** A point under the pointer, in client px: the hot line, or the channel. */
  track(clientX: number, clientY: number): void {
    if (!this.el) return;
    const hit = wheelHit(toChromePx(clientX) - this.cx, toChromePx(clientY) - this.cy, this.fig);
    if (hit !== null && 'hub' in hit) {
      this.setChannel(hit.hub);
      this.setHot(0);
      return;
    }
    this.setHot(hit === null ? 0 : hit.line);
  }

  /** The release: the hot line goes out when it can, and the wheel closes. */
  release(): void {
    const n = this.hotLine;
    if (n > 0) this.sendLine(n);
    else this.close();
  }

  /** Sends line n when the sender can name what it names, and closes. */
  sendLine(n: number): void {
    const pick = this.picks[n - 1] ?? null;
    const chan = this.chan;
    this.close();
    if (pick) this.d.send(n, chan, pick);
  }

  /** Takes the wheel off the screen. */
  close(): void {
    if (!this.el) return;
    this.lastChan = this.chan;
    this.el.remove();
    this.el = null;
    this.mode = null;
    this.pointerId = null;
    this.sticky = false;
    this.sectors = [];
    this.wordEls = [];
    this.halves = { squad: [], faction: [] };
    this.lineEl = null;
    this.hotLine = 0;
    window.removeEventListener('keydown', this.onKeyDown, true);
    window.removeEventListener('keyup', this.onKeyUp, true);
    window.removeEventListener('pointermove', this.onMove, true);
    window.removeEventListener('pointerup', this.onUp, true);
    window.removeEventListener('pointercancel', this.onCancel, true);
    window.removeEventListener('pointerdown', this.onPick, true);
    window.removeEventListener('blur', this.onBlur);
  }

  // --- the picture ---------------------------------------------------------

  private open(mode: Mode, px: number, py: number): void {
    const sheet = this.d.sheet();
    this.fig = wheelFig(sheet);
    const w = QUICK_WHEEL;
    const v = chromeViewport();
    const c = wheelCentre(px, py, v.w, v.h, this.fig, w.edgePx, w.lineDy + w.linePx);
    this.cx = c.x;
    this.cy = c.y;
    this.mode = mode;
    const ctx = this.d.ctx();
    this.picks = QUICK_LINES.map((_, i) => quickRef(i + 1, ctx));
    const squad = this.d.hasSquad();
    const tab = this.d.tabChannel();
    this.chan = tab ?? this.lastChan ?? (squad ? 'squad' : 'faction');
    if (!squad) this.chan = 'faction';
    this.el = this.draw(mode === 'key', squad);
    this.d.host.appendChild(this.el);
  }

  private draw(keys: boolean, squad: boolean): HTMLElement {
    const f = this.fig;
    const w = QUICK_WHEEL;
    const R = f.outer, r = f.inner, size = 2 * R + 8, c = R + 4;
    const root = document.createElement('div');
    root.id = 'quickwheel';
    root.setAttribute('role', 'menu');
    root.setAttribute('aria-label', S.wheel_label);
    root.style.left = `${this.cx - c}px`;
    root.style.top = `${this.cy - c}px`;
    const svg = document.createElementNS(SVG, 'svg');
    svg.setAttribute('width', String(size));
    svg.setAttribute('height', String(size));
    svg.setAttribute('viewBox', `0 0 ${size} ${size}`);
    const at = (deg: number, rr: number) => {
      const a = (deg * Math.PI) / 180;
      return `${(c + Math.cos(a) * rr).toFixed(1)} ${(c + Math.sin(a) * rr).toFixed(1)}`;
    };
    const gapDeg = (f.sectorGap * 180) / Math.PI;
    for (let n = 1; n <= QUICK_COUNT; n++) {
      const mid = sectorDeg(n, f);
      const a0 = mid - SPAN / 2 + gapDeg, a1 = mid + SPAN / 2 - gapDeg;
      const ri = r + f.gap;
      const path = document.createElementNS(SVG, 'path');
      path.setAttribute('d', `M ${at(a0, R)} A ${R} ${R} 0 0 1 ${at(a1, R)} L ${at(a1, ri)} A ${ri} ${ri} 0 0 0 ${at(a0, ri)} Z`);
      path.setAttribute('class', `sec${this.picks[n - 1] ? '' : ' off'}`);
      path.dataset.n = String(n);
      svg.appendChild(path);
      this.sectors.push(path);
      this.words(svg, n, mid, c, keys);
    }
    svg.appendChild(circle(c, r, 'hubbg'));
    const sqWord = text(c, c - r * 0.42, S.squad, `hw sq${squad ? '' : ' off'}`, w.hubPx);
    const faWord = text(c, c + r * 0.42, S.faction, 'hw fa', w.hubPx);
    this.halves.squad = [half(svg, c, r, true, squad ? '' : ' off'), sqWord];
    this.halves.faction = [half(svg, c, r, false, ''), faWord];
    svg.append(sqWord, faWord);
    const line = text(c, c + R + w.lineDy, '', 'ln', w.linePx);
    svg.appendChild(line);
    this.lineEl = line;
    root.appendChild(svg);
    this.paintChannel();
    return root;
  }

  /** The word of one sector, broken in two when it is long, with its key
   *  over it on a desktop. */
  private words(svg: SVGSVGElement, n: number, mid: number, c: number, keys: boolean): void {
    const f = this.fig;
    const w = QUICK_WHEEL;
    const a = (mid * Math.PI) / 180;
    const mr = (f.outer + f.inner + f.gap) / 2 + 4;
    const mx = c + Math.cos(a) * mr, my = c + Math.sin(a) * mr;
    const word = quickWord(n);
    const parts = word.split(' ');
    const lines = parts.length > 1 && word.length > 8
      ? [parts.slice(0, Math.ceil(parts.length / 2)).join(' '), parts.slice(Math.ceil(parts.length / 2)).join(' ')]
      : [word];
    const fs = w.wordPx;
    const top = my - ((lines.length - 1) * (fs + 1)) / 2 + (keys ? 5 : 0);
    const off = this.picks[n - 1] ? '' : ' off';
    if (keys) {
      const code = QUICK_LINES[n - 1].code;
      svg.appendChild(text(mx, top - fs - 2, KEYS.quick[code].label, `key${off}`, w.keyPx));
    }
    lines.forEach((s, j) => {
      const t = text(mx, top + j * (fs + 1), s, `w${off}`, fs);
      t.dataset.n = String(n);
      svg.appendChild(t);
      this.wordEls.push(t);
    });
  }

  private setHot(n: number): void {
    if (n === this.hotLine) return;
    if (this.hotLine > 0) this.sectors[this.hotLine - 1]?.classList.remove('on');
    this.hotLine = n;
    if (n > 0) this.sectors[n - 1]?.classList.add('on');
    for (const t of this.wordEls) t.classList.toggle('on', Number(t.dataset.n) === n);
    if (!this.lineEl) return;
    if (n === 0) {
      this.lineEl.textContent = '';
      return;
    }
    const pick = this.picks[n - 1];
    const pos = pick?.pos ? `  ${posText(pick.pos, this.d.look)}` : '';
    this.lineEl.textContent = pick ? `${quickText(n)}${pos}` : `${quickText(n)} — ${quickNeeds(n)}`;
  }

  private setChannel(ch: QuickChannel): void {
    if (ch === 'squad' && !this.d.hasSquad()) return;
    if (ch === this.chan) return;
    this.chan = ch;
    this.paintChannel();
  }

  /** Lights the half and the word of the channel. The draw calls it
   *  before the wheel stands, so it reads no element of the document. */
  private paintChannel(): void {
    for (const ch of ['squad', 'faction'] as const) {
      for (const el of this.halves[ch]) el.classList.toggle('on', ch === this.chan);
    }
  }

  // --- the listeners -------------------------------------------------------

  private readonly onMouse = (ev: PointerEvent): void => {
    if (ev.pointerType === 'mouse') this.mouse = { x: ev.clientX, y: ev.clientY };
  };

  private readonly onMove = (ev: PointerEvent): void => {
    // A sticky wheel between its two presses follows a hovering mouse.
    if (this.mode === 'touch' && ev.pointerId !== this.pointerId
      && !(this.pointerId === null && ev.pointerType === 'mouse')) return;
    if (this.mode === 'key' && ev.pointerType !== 'mouse') return;
    this.track(ev.clientX, ev.clientY);
  };

  private readonly onUp = (ev: PointerEvent): void => {
    if (ev.pointerId !== this.pointerId) return;
    swallow(ev);
    this.track(ev.clientX, ev.clientY);
    if (this.sticky && this.hotLine === 0) {
      // The tap of the QUICK press: the wheel waits for a press on a line.
      this.sticky = false;
      this.pointerId = null;
      window.addEventListener('pointerdown', this.onPick, true);
      return;
    }
    this.release();
  };

  /** The second press on a sticky wheel: it takes the pointer, and its
   *  release sends the line under it or closes the wheel. */
  private readonly onPick = (ev: PointerEvent): void => {
    window.removeEventListener('pointerdown', this.onPick, true);
    swallow(ev);
    this.pointerId = ev.pointerId;
    this.track(ev.clientX, ev.clientY);
  };

  private readonly onCancel = (ev: PointerEvent): void => {
    if (ev.pointerId === this.pointerId) this.close();
  };

  /** While the comma holds the wheel: a line key sends, Escape closes, and
   *  a repeat of the comma does nothing. The digits never reach the
   *  control groups of the map. */
  private readonly onKeyDown = (ev: KeyboardEvent): void => {
    if (matchKey(ev, { escape: KEYS.map.escape }) === 'escape') {
      swallow(ev);
      this.close();
      return;
    }
    if (matchKey(ev, { quick: KEYS.map.quick }) === 'quick') {
      swallow(ev);
      return;
    }
    const code = matchKey(ev, KEYS.quick);
    if (code === null) return;
    swallow(ev);
    const n = quickLineOf(code);
    if (n > 0) this.sendLine(n);
  };

  /** The release of the comma sends the hot line. */
  private readonly onKeyUp = (ev: KeyboardEvent): void => {
    if (matchKey(ev, { quick: KEYS.map.quick }) !== 'quick') return;
    swallow(ev);
    this.release();
  };

  private readonly onBlur = (): void => { this.close(); };
}

/**
 * The long press of the VIEW button (R1610, R1747). A touch or a pen press
 * on VIEW takes the press away from the dispatcher of the map buttons: a
 * press that rests `holdMs` opens the wheel and the same finger drives it,
 * and a shorter press calls `press` at its release, which opens or closes
 * the VIEW panel. A mouse press keeps the press rule of fixed chrome: the
 * desktop has the comma.
 */
export function installViewHold(
  host: HTMLElement, wheel: QuickWheel, press: () => void, holdMs: number = QUICK_WHEEL.holdMs,
): void {
  installHold(host, {
    pick: (ev) => (ev.pointerType === 'mouse' ? null : pressedControl(ev, 'button[data-act="view"]')),
    hold: (_, id) => { wheel.touchOpen(id); },
    press: () => { press(); },
  }, holdMs);
}

/**
 * The QUICK press of the chat input (R1799, card 18 of round 085). A press
 * on the QUICK button of `host` opens the wheel at once, for a mouse, a
 * pen and a finger; the held comma and the long press of VIEW stay
 * (R1610, R1611). The press never reaches the dispatcher of the chat, and
 * it keeps the focus where it is, so the keyboard of a telephone does not
 * close under the finger.
 */
export function installQuickPress(host: HTMLElement, wheel: QuickWheel): void {
  host.addEventListener('pointerdown', (ev) => {
    if (!pressedControl(ev, 'button[data-act="chatquick"]')) return;
    swallow(ev);
    wheel.pressOpen(ev.pointerId);
  }, true);
}

/** Takes an event the wheel answers away from the page: no default and
 *  no other listener. */
function swallow(ev: Event): void {
  ev.preventDefault();
  ev.stopPropagation();
}

// --- the SVG helpers --------------------------------------------------------

function circle(c: number, r: number, cls: string): SVGCircleElement {
  const el = document.createElementNS(SVG, 'circle');
  el.setAttribute('cx', String(c));
  el.setAttribute('cy', String(c));
  el.setAttribute('r', String(r));
  el.setAttribute('class', cls);
  return el;
}

/** One half of the hub: the upper half is SQUAD and the lower FACTION. */
function half(svg: SVGSVGElement, c: number, r: number, top: boolean, extra: string): SVGPathElement {
  const el = document.createElementNS(SVG, 'path');
  el.setAttribute('d', `M ${c - r} ${c} A ${r} ${r} 0 0 ${top ? 1 : 0} ${c + r} ${c} Z`);
  el.setAttribute('class', `half ${top ? 'sq' : 'fa'}${extra}`);
  svg.appendChild(el);
  return el;
}

function text(x: number, y: number, s: string, cls: string, size: number): SVGTextElement {
  const el = document.createElementNS(SVG, 'text');
  el.setAttribute('x', x.toFixed(1));
  el.setAttribute('y', y.toFixed(1));
  el.setAttribute('font-size', String(size));
  el.setAttribute('text-anchor', 'middle');
  el.setAttribute('dominant-baseline', 'middle');
  el.setAttribute('class', cls);
  el.textContent = s;
  return el;
}
